// External modules
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Store
import { selectUser } from '../../../store/users/userSlice';
import { getSelectedNodes } from '../../../store/positions/menuNodesSlice';
import { getSelectedMonitor, postTestCommand, setStatus, setResult, setSubCommand, setTestMessage, checkResultStatus } from '../../../store/positions/monitorsSlice';

// Helpers
import { notNull, isNull, notUndefinedNotNull } from '../../../lib/typeCheck';
import { status_states, test_messages, instrument_types } from '../../common/Types';

// Styled components
import styled from 'styled-components';
import { messages_capabilities, messages_common } from '../../common/types/Messages';
import { StyledContainer, StyledMiddleContainer } from '../../common/styles/devices/Container';
import { TopicLabel, HeadingLabel, GeneralLabel, CurrentFirmwareLabel, FirmwareSpan } from '../../common/styles/devices/Labels';

// Fields
import { StandardMainDisplayMessage } from '../../common/fields/devices/DisplayMessages';
import { Row, Column } from '../../common/fields/PageUtils';
import { TestButton } from '../../common/fields/devices/Buttons';

// Monitor firmware info
import { findFirmware } from '../../../store/positions/firmwareSlice';
import { RootState } from '../../../store/store';

const Br = styled.div<{margin?: string}>`
  margin: ${props => props.margin? props.margin : '0 0 31px 0'};
`;

const typeCheck = (value: any) => {
  if(notUndefinedNotNull(value)){
    if(value === ''){
      return 'Unavailable'
    }
    return value
  } 
  
  return 'Unavailable';
}

const row_margin = '30px 0 0 100px';
const column_width = '100%';

// Define a variable to keep track on 
// interval id of `setInterval` function call
let interval_id;

export function Capabilities({ path: string }) {    

    const dispatch = useDispatch();    
    
    // Get the selected station's details
    const selectedNode = useSelector(getSelectedNodes);
    const node = selectedNode.length> 0? selectedNode[0] : null;    
    
    // Get current user
    const user = useSelector(selectUser);    

    // Get the selected station's monitor details
    const selectedMonitor =  useSelector(getSelectedMonitor(notNull(node)? node.monitor_id : null));
    const monitor = selectedMonitor.length > 0 ? selectedMonitor[0] : null;

    // Re-fetch firmware information via api call whenever monitor changes
    useEffect(() => {
      if (monitor) {
        // Fetch firmware data when component mounts or monitor changes
        dispatch(findFirmware(monitor.id));
      }
    }, [monitor, dispatch]);
    
    // Get firmwareData from state.firmware
    const firmwareData = useSelector((state: RootState) => state.firmware);

    // Initiate state variables
    const [currentFirmware, setCurrentFirmware] = useState('');
    const [latestFirmware, setLatestFirmware] = useState('');
    const [majorValue, setMajorValue] = useState(0);
    const [minorValue, setMinorValue] = useState(0);
    const [revisionValue, setRevisionValue] = useState(0);
    const [majorValueColour, setMajorValueColour] = useState('');
    const [minorValueColour, setMinorValueColour] = useState('');
    const [revisionValueColour, setRevisionValueColour] = useState('');

    // Reset colours to default upon monitor change
    useEffect(() => {
      if (monitor) {
        setMajorValueColour('');
        setMinorValueColour('');
        setRevisionValueColour('');
      }
    }, [monitor, dispatch]);

    // If firmwareData changes, set the new current and latest firmware strings
    useEffect(() => {
        if (!Array.isArray(firmwareData)) {
            setCurrentFirmware(firmwareData['current'] || '');
            setLatestFirmware(firmwareData['latest'] || '');
        }
    }, [firmwareData]);

    // Split current and latest firmwares into their major, minor and revision values
    // Then set these variables and compare them
    // If major version is lower, all components become red. If minor is lower, minor and revision are orange etc.
    useEffect(() => {
      if (currentFirmware && latestFirmware) {
          const [currentMajor, currentMinor, currentRevision] = currentFirmware.split('.').map(Number);
          const [latestMajor, latestMinor, latestRevision] = latestFirmware.split('.').map(Number);

          setMajorValue(currentMajor);
          setMinorValue(currentMinor);
          setRevisionValue(currentRevision);

          if (currentMajor < latestMajor) {
              setMajorValueColour('red');
              setMinorValueColour('red');
              setRevisionValueColour('red');
          } else if (currentMinor < latestMinor) {
              setMinorValueColour('orange');
              setRevisionValueColour('orange');
          } else if (currentRevision < latestRevision) {
              setRevisionValueColour('goldenrod');
          }
      }
  }, [currentFirmware, latestFirmware]);


    // A state to track the very first instance of a `success` response from the POST request to the backend
    const [successStatus, setSuccessStatus] = useState(false);
    // A state to track the very first instance of getting back a result from GET request    
    const [resultStatus, setResultStatus] = useState(false);   
    // A state to track the interval_id of setInterval function
    const [currentIntervalID, setCurrentIntervalID] = useState(0);
    // Tracks the state of cancel interval when the setInterval needs to be stopped
    const [cancelInterval, setCancelInterval] = useState(false);
 
    // On click of the `Test Now` button
    const toggleLoading = () => {

        // Set the details as follow
        dispatch(setStatus({id: node.monitor_id, status: status_states.pending}));
        dispatch(setResult({id: node.monitor_id, result: false}));
        dispatch(setSubCommand({id: node.monitor_id, sub_command: null}))

        // Set the display string to `Loading...`
        dispatch(setTestMessage({id: node.monitor_id, test_message: test_messages.loading}));

        // Clear states and set values to default
        setSuccessStatus(false);
        setResultStatus(false);
        setCancelInterval(false);
        
        // Dispatch the test command to the selected monitor id     
        dispatch(postTestCommand(selectedMonitor[0])); 
    }

    // This expression will toggle the states when 
    // `selectedMonitor[0].status === 'success' && !successStatus`
    const triggerStates = () => {          
      setResultStatus(true);            
      setSuccessStatus(true);      
    }

    // This expression will toggle the states when 
    // `selectedMonitor[0].result === true && selectedMonitor[0].status === 'success' && resultStatus === true`
    const triggerStop = () => {
      
      setCancelInterval(true);
      setResultStatus(false);
    }
    
    // This expression will call the calling function for GET requests
    const triggerFunction = () => {
      
      if(notNull(node)) {        
        checkResultStatus(dispatch, user, node.monitor_id);
      }
      
    }
    
    // Depending on the state of `resultStatus` and `cancelInterval`
    // This hook will starts or clears the setInterval function
    useEffect(() => {      
      
      if(resultStatus){     
        interval_id = setInterval(triggerFunction, 10000);        
        setCurrentIntervalID(interval_id);        
      }
      
      if (cancelInterval) {        
        clearInterval(interval_id);     
      }
    }, [resultStatus, cancelInterval]);
    
    // Check if the instrument_type is a collocated weather station
    const has_colocated_met_type = notNull(node) && (node.instrument_type === instrument_types.met && notNull(node.co_located_id));
      
    return (
        <StyledContainer>
            <StyledMiddleContainer height='100%'>

                {/* Display Select Message if no station is selected from the left panel */}
                {isNull(node) ? <StandardMainDisplayMessage message={messages_common.select}/> : null}

                {/* Display Colocated Message if the instrument_type is a collocated weather station */}
                {notNull(node) && has_colocated_met_type ? <StandardMainDisplayMessage message={messages_capabilities.co_located}/> : null}

                {/* Display monitor details if a sation has selected which is not a collocated weather station */}
                {notNull(node) && !has_colocated_met_type ?
                    
                    <StyledContainer>   
                      
                        <Column>    
                          {notUndefinedNotNull(monitor.boot_log)? <Br margin='0 0 510px 0'/> : <Br margin='0 0 100px 0'/>}
                          <Row margin={row_margin}>                            
                            <Column width={column_width}>
                              <TopicLabel>ID numbers</TopicLabel>
                              <HeadingLabel>Position</HeadingLabel>
                              <HeadingLabel>Monitor</HeadingLabel>
                            </Column>

                            <Column width={column_width}>
                              <Br/>
                              <GeneralLabel>{node.id}</GeneralLabel>
                              <GeneralLabel>{monitor.id}</GeneralLabel>
                            </Column>
                          </Row>

                          {notUndefinedNotNull(monitor.boot_log)?
                          <Row margin={row_margin}>                   
                            <Column width={column_width}>
                              <TopicLabel>Owasys</TopicLabel>
                              <HeadingLabel>Serial Number</HeadingLabel>
                              <HeadingLabel>Current Firmware</HeadingLabel>                                 
                              <HeadingLabel>Latest Available Firmware</HeadingLabel>
                            </Column>
                            
                            <Column width={column_width}>
                              <Br/>
                              <GeneralLabel>{typeCheck(monitor.boot_log.owasys)}</GeneralLabel>
                              <CurrentFirmwareLabel>
                                <FirmwareSpan color={majorValueColour}>{majorValue}</FirmwareSpan>.
                                <FirmwareSpan color={minorValueColour}>{minorValue}</FirmwareSpan>.
                                <FirmwareSpan color={revisionValueColour}>{revisionValue}</FirmwareSpan>
                              </CurrentFirmwareLabel>
                              <GeneralLabel>{latestFirmware}</GeneralLabel>
                            </Column>                            
                          </Row>  
                          :
                          null }

                          <Row margin={row_margin}>
                            <Column width={column_width}>
                              {monitor.make === 'Rion'? <TopicLabel>Rion</TopicLabel> : <TopicLabel>Instrument</TopicLabel>}
                              <HeadingLabel>Serial Number</HeadingLabel>
                              <HeadingLabel>Model</HeadingLabel>
                              <HeadingLabel>Make</HeadingLabel>
                              {monitor.id !== null && monitor.api_key_id !== null && monitor.type_id !== instrument_types.met && user.permitted_actions.includes("manage_monitors")?<HeadingLabel>Next Calibration due</HeadingLabel> : null}                                    
                            </Column>
                            <Column width={column_width}>
                              <Br/>
                              <GeneralLabel>{typeCheck(monitor.serial)}</GeneralLabel>
                              <GeneralLabel>{typeCheck(monitor.model)}</GeneralLabel>
                              <GeneralLabel>{typeCheck(monitor.make)}</GeneralLabel>
                              {monitor.type_id !== instrument_types.met?<GeneralLabel>{monitor.next_calibration ? monitor.next_calibration : `Not Set`}</GeneralLabel> : null}
                            </Column>
                          </Row>
                          
                          {notUndefinedNotNull(monitor.boot_log)?
                          <Row margin={row_margin}>                           
                            <Column width={column_width}>
                              <TopicLabel>Instrument Options</TopicLabel>
                              <HeadingLabel>Ex</HeadingLabel>
                              <HeadingLabel>Ft</HeadingLabel> 
                              <HeadingLabel>Nl</HeadingLabel>                              
                              <HeadingLabel>Rt</HeadingLabel>                              
                              <HeadingLabel>Vm</HeadingLabel>
                              <HeadingLabel>Wr</HeadingLabel>                            
                            </Column>                            
                                  
                            <Column width={column_width}>   
                              <Br/>                 
                              <GeneralLabel>{typeCheck(monitor.boot_log.ex)}</GeneralLabel> 
                              <GeneralLabel>{typeCheck(monitor.boot_log.ft)}</GeneralLabel>
                              <GeneralLabel>{typeCheck(monitor.boot_log.nl)}</GeneralLabel> 
                              <GeneralLabel>{typeCheck(monitor.boot_log.rt)}</GeneralLabel>                              
                              <GeneralLabel>{typeCheck(monitor.boot_log.vm)}</GeneralLabel>
                              <GeneralLabel>{typeCheck(monitor.boot_log.wr)}</GeneralLabel>
                            </Column>
                          </Row>
                          : null}

                          {notUndefinedNotNull(monitor.boot_log)?
                          <Row margin={row_margin}>                            
                            <Column width={column_width}>
                              <TopicLabel>RV-50</TopicLabel>
                              <HeadingLabel>Serial Number</HeadingLabel>
                              <HeadingLabel>Sim</HeadingLabel>
                            </Column>
                                                      
                            <Column width={column_width}>
                              <Br/>
                              <GeneralLabel>{typeCheck(monitor.boot_log.rv50)}</GeneralLabel> 
                              <GeneralLabel>{typeCheck(monitor.boot_log.sim)}</GeneralLabel> 
                            </Column>                           
                          </Row>
                          : null }

                          <Row margin={row_margin}>
                            <Column width={column_width}>
                              <TopicLabel>General</TopicLabel>
                              <HeadingLabel>Power Saving</HeadingLabel>
                              {node.instrument_type === instrument_types.noise? 
                                <HeadingLabel>NX-42WR Audio</HeadingLabel>
                              : null}
                              <HeadingLabel>Country Code</HeadingLabel>
                              <HeadingLabel>IP Address</HeadingLabel>
                              <HeadingLabel>Phone Number</HeadingLabel>
                            </Column> 
                            <Column width={column_width}>
                              <Br/>
                              <GeneralLabel>{notNull(node.features.use_power_schedules) ? (node.features.use_power_schedules === true? 'Enabled': 'Disabled'): 'Not Capable'}</GeneralLabel>
                              {node.instrument_type === instrument_types.noise?
                                <GeneralLabel>{notNull(node.features.use_rawaudio_schedules) ? (node.features.use_rawaudio_schedules === true? 'Enabled': 'Disabled'): 'Not Capable'}</GeneralLabel>
                              : null }
                              <GeneralLabel>{typeCheck(monitor.countrycode)}</GeneralLabel>
                              <GeneralLabel>{typeCheck(monitor.ipaddress)}</GeneralLabel>
                              <GeneralLabel>{typeCheck(monitor.phonenumber)}</GeneralLabel>
                            </Column>
                          </Row>

                          <Row margin={row_margin}>
                            <Column width={column_width}>
                              <TopicLabel>Position Status</TopicLabel>
                              <HeadingLabel>State</HeadingLabel>                              
                            </Column>

                            <Column width={column_width}>
                              <Br/>
                              <GeneralLabel>{monitor.state === 'active'? 'Active' : 'Inactive'}</GeneralLabel>
                            </Column>
                          </Row> 

                          <Row margin={row_margin}>
                            <Column width={column_width}>
                              {notUndefinedNotNull(monitor.boot_log)? <HeadingLabel><TestButton onClick={toggleLoading} text='Test RV-50'/></HeadingLabel> : null }
                            </Column>

                            <Column width={column_width}>
                              {/* If monitor status is 'success' and 'successStatus' state is false, toggle other states to start sending GET requests simultaneously every 5s
                                By doing this it is able to identify the very first instance of reporting a 'success' response
                                It is mandotory to look for the 'successStatus' state, without that following condition will trigger the 'triggerState' expression when
                                the monitor status is equal to success, which will create an infinite hooks calls and causes an error*/}
                              {(monitor.status === status_states.success && !successStatus)? triggerStates() : null}

                              {/* When frontend receives a response from the monitor, Sending GET request every 5s needs to be stopped. 
                              For that following  following condition should be satisfied
                              It is important to checks for the 'resultStatus' state to mitigate infinite hook calls and erros*/}
                              {(monitor.result === true && monitor.status === status_states.success && resultStatus === true) ? 
                                triggerStop() : null }

                              {notUndefinedNotNull(monitor.boot_log)? 
                                <GeneralLabel margin='3px 0 10px 20px'>
                                  {/* Checks if a test_message exist */}
                                  {notUndefinedNotNull(monitor.test_message) ?
                                    monitor.test_message

                                  // Else display the Test Now Message
                                  : test_messages.test_now}
                                </GeneralLabel>
                              : null }
                            </Column>
                          </Row>
                       
                        </Column>      
                    </StyledContainer>
                    : null}
            </StyledMiddleContainer>
        </StyledContainer>      
    
    
    )
}

